<script>
import { ref, onBeforeMount, computed } from 'vue'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { getRestoPaymentSettingFn } from '@/services/api/restApiCalls'
import MolliePaymentV1 from './MolliePaymentV1.vue'
import MolliePaymentV2 from './MolliePaymentV2.vue'

export default {
  components: {
    MolliePaymentV1,
    MolliePaymentV2
  },

  props: {
    beforePlaceOrder: {
      type: Function,
      default: null
    }
  },

  setup(props, { expose }) {
    const restoStore = useRestoGLobalStore()

    const mollieProfileId = ref('')
    const mollieComponentVersion = ref('v1')
    const mollieTestmode = ref(false)
    const mollieConnectionError = ref('')
    const isLoaded = ref(false)
    const childRef = ref(null)

    const showMollieV1 = computed(() => {
      return !mollieConnectionError.value && mollieComponentVersion.value !== 'v2'
    })

    const showMollieV2 = computed(() => {
      return !mollieConnectionError.value && mollieComponentVersion.value === 'v2'
    })

    const payButtonDisabled = computed(() => {
      if (mollieConnectionError.value) return true
      return childRef.value?.payButtonDisabled !== false
    })

    const handleCardPayment = () => {
      childRef.value?.handleStripePayment?.()
    }

    onBeforeMount(async () => {
      let settings = await getRestoPaymentSettingFn(restoStore?.RESTO_ID)
      if (!settings?.gateway) {
        settings = await getRestoPaymentSettingFn(restoStore?.RESTO_ID)
      }

      mollieProfileId.value = settings?.mollie_profile_id || ''
      mollieComponentVersion.value = settings?.mollie_component_version || 'v1'
      mollieTestmode.value = !!settings?.mollie_testmode
      const mollieIsConnected = !!settings?.mollie_is_connected
      const mollieCanReceivePayments = !!settings?.mollie_can_receive_payments
      const mollieOnboardingStatus = settings?.mollie_onboarding_status || ''

      if (!mollieIsConnected) {
        mollieConnectionError.value =
          'Card payments are not available — this restaurant has not connected Mollie yet.'
      } else if (!mollieCanReceivePayments) {
        mollieConnectionError.value =
          'Card payments are temporarily unavailable while Mollie onboarding completes.'
        if (mollieOnboardingStatus) {
          mollieConnectionError.value += ` Status: ${mollieOnboardingStatus}.`
        }
      } else if (!mollieProfileId.value && mollieComponentVersion.value !== 'v2') {
        mollieConnectionError.value =
          'Card payments are not configured — no Mollie profile is selected for this restaurant.'
      }

      isLoaded.value = true
    })

    expose({
      handleCardPayment,
      payButtonDisabled
    })

    return {
      isLoaded,
      mollieProfileId,
      mollieTestmode,
      mollieConnectionError,
      showMollieV1,
      showMollieV2,
      childRef,
      beforePlaceOrder: props.beforePlaceOrder
    }
  }
}
</script>

<template>
  <div v-if="isLoaded" class="mollie-card-gateway">
    <div
      v-if="mollieConnectionError"
      class="p-4 text-center text-red-600 dark:text-red-400 text-sm font-medium"
    >
      {{ mollieConnectionError }}
    </div>
    <MolliePaymentV2
      v-else-if="showMollieV2"
      ref="childRef"
      :before-place-order="beforePlaceOrder"
    />
    <MolliePaymentV1
      v-else-if="showMollieV1"
      ref="childRef"
      :profile-id="mollieProfileId"
      :testmode="mollieTestmode"
      :before-place-order="beforePlaceOrder"
    />
  </div>
  <div v-else class="p-4 text-center text-gray-500 dark:text-gray-400">
    Loading payment details...
  </div>
</template>
